import React, { useEffect } from "react";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Container,
  FormFeedback,
  Form,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { addLog, getLogs } from "../../../api/log"; // Import getLogs

const AddLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dealId } = useParams();

  const validationSchema = Yup.object().shape({
    delivered: Yup.boolean().default(false),
    pending: Yup.boolean().default(false),
    notes: Yup.string()
      .required(t("Notes are required"))
      .max(500, t("Too long"))
      .test(
        "unique-note",
        t("A log with the same note already exists for this deal"),
        async (value) => {
          if (!value) return true;
          try {
            const response = await getLogs(
              { dealId, notes: value },
              { page: 1, limit: 1 }
            );
            return response.data.total === 0;
          } catch (error) {
            return true;
          }
        }
      ),
    files: Yup.mixed().nullable(),
  });

  const validation = useFormik({
    initialValues: {
      delivered: false,
      pending: false,
      notes: "",
      files: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("delivered", values.delivered.toString());
      formData.append("pending", values.pending.toString());
      formData.append("notes", values.notes);

      if (values.files) {
        for (let i = 0; i < values.files.length; i++) {
          formData.append("files", values.files[i]);
        }
      }

      setData({ data: formData, type: "add" });
    },
  });

  const { setData, loading, error, success } = useResponse(
    (formData) => addLog(formData, dealId),
    () => navigate(`/deals/show/${dealId}`)
  );

  const getErrorMessage = () => {
    const message = error?.response?.data?.message;

    if (typeof message === "string" && message.includes("unique")) {
      return t("A log with the same note already exists for this deal");
    }

    return t("An error occurred");
  };

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title={`${t("Add")} ${t("Log")}`} />

        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Row>
              <Col md="6" className="d-flex align-items-center mb-3">
                <FormGroup check inline>
                  <Input
                    type="checkbox"
                    name="delivered"
                    id="delivered"
                    onChange={(e) => {
                      validation.setFieldValue("delivered", e.target.checked);
                      if (e.target.checked) {
                        validation.setFieldValue("pending", false);
                      }
                    }}
                    checked={validation.values.delivered}
                  />
                  <Label for="delivered" check className="mb-0 ms-2">
                    {t("Delivered")}
                  </Label>
                </FormGroup>
              </Col>

              <Col md="6" className="d-flex align-items-center mb-3">
                <FormGroup check inline>
                  <Input
                    type="checkbox"
                    name="pending"
                    id="pending"
                    onChange={(e) => {
                      validation.setFieldValue("pending", e.target.checked);
                      if (e.target.checked) {
                        validation.setFieldValue("delivered", false);
                      }
                    }}
                    checked={validation.values.pending}
                  />
                  <Label for="pending" check className="mb-0 ms-2">
                    {t("Pending")}
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <Col md="12">
              <FormGroup className="mb-3">
                <Label>{t("Notes")}</Label>
                <Input
                  name="notes"
                  placeholder={t("Write any notes")}
                  type="textarea"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.notes}
                  invalid={
                    !!(validation.touched.notes && validation.errors.notes)
                  }
                  rows={5}
                />
                <FormFeedback>{validation.errors.notes}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup className="mb-3">
                <Label>{t("Upload Files")}</Label>
                <Input
                  name="files"
                  type="file"
                  multiple
                  onChange={(event) => {
                    validation.setFieldValue(
                      "files",
                      event.currentTarget.files
                    );
                  }}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(validation.touched.files && validation.errors.files)
                  }
                />
                <FormFeedback>{validation.errors.files}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormButtonHandler btnTxt={t("Add")} loading={loading} />

          <ResultHandler
            error={error}
            success={success}
            successMsg={t("Created Successfully!")}
            errorMsg={getErrorMessage()}
          />
        </Form>
      </Container>
    </div>
  );
};

export default AddLog;
